/**
 * Boolean setting with a native checkbox exposed with switch semantics.
 * @element qds-switch
 * @attr {boolean} checked - Default/reset checked state; checked property is current state.
 * @attr {string} value - Native submission value when checked; default on.
 * @attr {string} name - Submitted field name.
 * @attr {string} label - Visible fallback label; default slot also supports label content.
 * @attr {string} aria-label - Explicit accessible name.
 * @attr {string} hint - Helper text.
 * @attr {string} error-text - Validation message override.
 * @attr {boolean} disabled - Not interactive or submitted.
 * @attr {boolean} readonly - Preserves state while remaining focusable/submitted.
 * @attr {boolean} required - Must be checked to be valid.
 * @attr {boolean} invalid - Author-supplied invalid state.
 * @attr {'sm'|'md'|'lg'} size - Visual control size; row remains at least 44px.
 * @attr {'blur'|'input'|'submit'} validate-on - Error display timing; default blur.
 * @property {boolean} checked - Current boolean state; silent on programmatic assignment.
 * @property {boolean} defaultChecked - Attribute-backed reset state.
 * @property {string} value - Native checked submission value; separate from boolean checked.
 * @slot - Visible setting label; overrides the label attribute fallback.
 * @csspart container - Field container.
 * @csspart row - Clickable native label.
 * @csspart input - Native switch control.
 * @csspart track - Visual track.
 * @csspart thumb - Visual thumb.
 * @csspart label - Label slot.
 * @csspart hint - Helper text.
 * @csspart error - Validation text.
 * @fires input - User toggle; detail { component, checked, value }.
 * @fires change - User toggle; detail { component, checked, value }.
 * @fires qds-validity-change - Validity or message change.
 */
import { QdsNativeField, type FieldValidity } from '../shared/native-field.js';
import { isAttrTruthy, reflectBoolean } from '../shared/form-control.js';

export class QdsSwitch extends QdsNativeField {
  static override get observedAttributes(): string[] { return [...super.observedAttributes, 'checked', 'value']; }
  private _checked = false;
  protected override get _upgradeNames(): string[] { return ['checked', 'defaultChecked', 'value']; }
  get checked(): boolean { return this._checked; }
  set checked(value: boolean) { this._dirty = true; this._checked = Boolean(value); this._sync(); }
  get defaultChecked(): boolean { return isAttrTruthy(this.getAttribute('checked')); }
  set defaultChecked(value: boolean) { reflectBoolean(this, 'checked', value); }
  get value(): string { return this.getAttribute('value') ?? 'on'; }
  set value(value: string) { this.setAttribute('value', String(value ?? '')); }
  override attributeChangedCallback(): void { if (!this._dirty) this._checked = this.defaultChecked; super.attributeChangedCallback(); }
  override click(): void { this._control?.click(); }
  protected _render(): void {
    if (!this._dirty) this._checked = this.defaultChecked;
    this.shadowRoot!.innerHTML = `<style>${this._styles}
      :host { width: auto; } .row { min-height: 44px; display: inline-flex; align-items: center; gap: 10px; cursor: pointer; position: relative; color: var(--theme-text-primary, #333); font-size: 14px; }
      input { position: absolute; width: 1px; height: 1px; opacity: 0; }
      .track { box-sizing: border-box; display: inline-flex; flex: 0 0 auto; align-items: center; width: 40px; height: 24px; padding: 3px; border: 1px solid var(--theme-border-secondary, #7b879b); border-radius: 999px; background: var(--theme-background-secondary, #f8f8f8); transition: background-color .2s ease; }
      .thumb { width: 16px; height: 16px; border-radius: 50%; background: var(--theme-text-secondary, #555); transition: transform .2s ease; }
      input:checked + .track { background: var(--theme-accent-primary, #445e89); border-color: var(--theme-accent-primary, #445e89); }
      input:checked + .track .thumb { transform: translateX(16px); background: var(--theme-text-on-accent-primary, #fff); }
      :host(:dir(rtl)) input:checked + .track .thumb { transform: translateX(-16px); }
      input:focus + .track { outline: 2px solid var(--theme-accent-primary, #445e89); outline-offset: 2px; }
      input:disabled ~ * { cursor: not-allowed; }
      :host([size="lg"]) .row { min-height: 48px; font-size: 16px; }
      :host([size="sm"]) .row { font-size: 13px; }
      @media (forced-colors: active) { .track { border-color: ButtonText; } .thumb { background: ButtonText; } input:checked + .track { background: Highlight; border-color: Highlight; } input:checked + .track .thumb { background: HighlightText; } }
    </style><div part="container"><label class="row" part="row" for="${this._id}"><input id="${this._id}" type="checkbox" role="switch" part="input"><span class="track" part="track" aria-hidden="true"><span class="thumb" part="thumb"></span></span><slot part="label"><span class="label-text"></span></slot><span class="qds-required-marker" aria-hidden="true">*</span></label><div class="qds-feedback"><div class="qds-hint" part="hint" id="${this._id}-hint"></div><div class="qds-error" part="error" id="${this._id}-error" aria-live="polite"></div></div></div>`;
    this._control = this.shadowRoot!.querySelector('input')!;
  }
  protected _syncControl(): void {
    const input = this._control as HTMLInputElement;
    input.checked = this.checked;
    input.value = this.value;
    input.setAttribute('aria-checked', String(this.checked));
  }
  private _onClick = (event: Event): void => { if (this.disabled || this.readonly) event.preventDefault(); };
  private _onInput = (event: Event): void => event.stopPropagation();
  private _onChange = (event: Event): void => {
    event.stopPropagation();
    if (this.disabled || this.readonly) { this._sync(); return; }
    this._dirty = true;
    this._checked = (this._control as HTMLInputElement).checked;
    if (this.validateOn === 'input') this._showError = true;
    this._sync();
    const detail = { checked: this.checked, value: this.value };
    this._emit('input', detail);
    this._emit('change', detail);
  };
  protected _bind(): void {
    this._control!.addEventListener('click', this._onClick);
    this._control!.addEventListener('input', this._onInput);
    this._control!.addEventListener('change', this._onChange);
    this._control!.addEventListener('blur', this._onBlur);
  }
  protected _unbind(): void {
    this._control?.removeEventListener('click', this._onClick);
    this._control?.removeEventListener('input', this._onInput);
    this._control?.removeEventListener('change', this._onChange);
    this._control?.removeEventListener('blur', this._onBlur);
  }
  protected _formState(): string { return this.checked ? 'checked' : 'unchecked'; }
  protected _formValue(): string | null { return this.checked ? this.value : null; }
  protected _reset(): void { this._checked = this.defaultChecked; }
  protected _restore(state: string): void { this._checked = state === 'checked'; }
  protected _constraints(): { flags: Partial<FieldValidity>; message: string } {
    return this.required && !this.checked ? { flags: { valueMissing: true }, message: 'Please turn on this setting.' } : { flags: {}, message: '' };
  }
}
if (!customElements.get('qds-switch')) customElements.define('qds-switch', QdsSwitch);
